export default interface BookingData {
    bookingId: number;
    data: {
        alertApisData: boolean;
        allowApisData: boolean;
        allowClaim: boolean;
        allowDigitalTourGuide: boolean;
        allowExtraServices: boolean;
        allowExtraServicesExcursion: boolean;
        allowFTCRebooking: boolean;
        allowHotelRebooking: boolean;
        allowNameChange: boolean;
        allowOnSpot: boolean;
        allowRailAndFly: boolean;
        allowRailAndFlyInbound: boolean;
        allowRailAndFlyOutbound: boolean;
        allowSeatReservationInbound: boolean;
        allowSeatReservationOutbound: boolean;
        allowSeatReservations: boolean;
        allowTransfer: boolean;
        allowedInteractions: AllowedInteractions;
        allowedPaymentMethods: AllowedPaymentMethods;
        emergencyContactData: EmergencyContactData | [];
        flightDetailParams?: unknown;
        frontendParams: FrontendParams;
        gotApisData: boolean;
        haveToFTCRebooking: boolean;
        haveToFlightAlternativesFromMail: boolean;
        isAllPossibleSeatsBooked: boolean;
        isAllPossibleSeatsBookedInbound: boolean;
        isAllPossibleSeatsBookedOutbound: boolean;
        isFlightCancelledInbound: boolean;
        isFlightCancelledOutbound: boolean;
        isInternal: boolean;
        serviceInformation: ServiceInformation;
        searchParams: SearchParams | null;
        tourGuideData: TourGuideData;
        travelType: 'package' | 'hotel' | 'trip';

        // properties set in frontend
        bookingCancelled?: boolean;
    };
    travelStart: string;

    // properties set in frontend
    [key: string]: unknown;
}

export interface ServiceFlags {
    accommodation: boolean;
    transport: boolean;
    cruise: boolean;
}

export interface FrontendParams {
    apis?: ApisDetails;
    cruise: CruiseDetails | null;
    customer: unknown;
    digitalTourGuide: boolean;
    flightDetails: FlightDetails;
    hotelDetails?: HotelDetails[];
    transferDetails?: TransferDetails;
    excursions?: ExcursionDetails[];
    seatReservation?: SeatReservationDetails;
    mediatedServices: ServiceFlags;
    postPayServices: ServiceFlags;
    paymentDetails: PaymentDetails;
    price: PriceDetails;
}

export interface EmergencyContactData {
    name: string;
    mail: string;
    phone: string;
}

interface AllowedInteractions {
    cancellation: {
        completeCancellation: boolean;
        partialCancellation: {
            excursion: boolean;
            seatReservation: boolean;
        };
    };
    login: {
        allowed: boolean;
        reason: string;
    };
    payment: {
        alertDeposit: boolean;
        alertFull: boolean;
        deposit: boolean;
        full: boolean;
        refund: boolean;
    };
    onSpot: boolean;
}

interface AllowedPaymentMethods {
    applePaySpsAllowed: boolean;
    ch24PayAllowed: boolean;
    creditCardAllowed: boolean;
    directDebitAllowed: boolean;
    googlePaySpsAllowed: boolean;
    payPalSpsAllowed: boolean;
}

interface ServiceInformation {
    package: { state: 'OK' | 'MO' | 'TB' | 'XX' | 'SB' | 'RQ' };
    bookedSeats?: {
        [key: string]: {
            seat: string;
        };
    };
    excursions?: {
        bookingId: string;
    }[];
}

const BookingStates = ['OK', 'XX', 'NO', 'over'] as const;

export type BookingState = (typeof BookingStates)[number];

export const isBookingState = (value: string): value is BookingState => {
    return BookingStates.some((state) => state === value);
};

interface SearchParams {
    retDate: string;
    depDate: string;
}

interface TourGuideData {
    registered: boolean;
}

interface ApisDetails {
    [key: string]: {
        'vollständiger Name lt. Reisepass': string;
        Geburtsdatum: string;
        Geschlecht: string;
        'Ausstellungsland Reisepass': string;
        Nationalität: string;
        Reisepassnummer: string;
        'Ablaufdatum Reisepass': string;
    };
}

interface HotelDetails {
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    areaDescription: string;
    atCode: string;
    category: string;
    checkIn: string;
    checkOut: string;
    countryDescription: string;
    description: string;
    latitude: number;
    longitude: number;
    locationDescription: string;
    mainImage: string;
    meal: string;
    mediated: boolean;
    name: string;
    persons: unknown;
    postPay: boolean;
    remark: string;
    room: string;
    rooms: unknown;
    tel: string;
}

interface PaymentDetails {
    accountNumber: string;
    accountOwner: string;
    additional: string;
    bankName: string;
    pending: boolean;
    type: string; // ch24pay || ...
}

interface PriceDetails {
    currency: string;
    depositAmount: number;
    fullyPaid: boolean;
    mainPackageFullyPaid: boolean;
    mediatedPrice: number;
    open: number;
    packagePrice: number;
}

interface TransferDetails {
    tariff: string;
}

interface ExcursionDetails {
    state: string;
}

interface SeatReservationDetails {
    [key: string]: {
        state: string;
    };
}

interface FlightDetails {
    inbound: FlightSegment[];
    outbound: FlightSegment[];
}

export interface FlightSegment {
    airline: string;
    arrAirportCode: string;
    arrAirportDescription: string;
    arrDate: string;
    arrTime: string;
    carrier: string;
    depAirportCode: string;
    depAirportDescription: string;
    depDate: string;
    depTime: string;
    duration: number; // in seconds
    flightNumber: string;
    mediated: boolean;
    postPay: boolean;
    remark: string;
    seatReservation: boolean;
    serviceStatus: string;
}

interface CruiseDetails {
    mediated: boolean;
    postPay: boolean;
    persons: unknown;
    checkIn: string;
    checkOut: string;
    shipName: string;
    atsCode: string;
    providerName: string;
    supplierBookingProviderCode: string;
    itinierary: unknown;
    room: unknown;
    oci: unknown;
    additionalTariffInfo: unknown;
}
